import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import { ConfigGraphCard } from "@types"

// =====================
// Type definitions des réponses des services
// =====================
type GraphResponse = {
    /**
     * Données du graphique Plotly au format JSON string
     */
    data: string
    /**
     * Liste des sources de données
     */
    sources?: string[]
}

export type GraphListResponse = ConfigGraphCard[]

// =====================
// Service
// =====================

/**
 * Le service permettant de gérer l'affichage des graphiques
 */
export class GraphService {
    /**
     * Récupère la liste des graphiques disponibles
     * @param abordController - Contrôleur d'annulation
     * @returns
     */
    async getGraphList(
        abordController?: AbortController
    ): Promise<GraphListResponse> {
        return handleServiceCall<GraphListResponse>(async () => {
            const { data } = await axios.get(`/api/metier/graph`, {
                signal: abordController?.signal,
            })
            return data?.data
        })
    }
    /**
     * Crée un graphique pour les données d'un bâtiment récupérées par son identifiant
     * @param _id_building - Identifiant du bâtiment
     * @param date_from - Date de début au format YYYY-MM-DD
     * @param date_to - Date de fin au format YYYY-MM-DD
     * @param freq - Fréquence (`hourly`, `daily`, `weekly`, `monthly`)
     * @param datasets - Les datasets à afficher
     */
    async createGraphByBuildingId(
        _id_building: string,
        date_from: string,
        date_to: string,
        freq: string,
        datasets: any[],
        {
            ylabel,
            abordController,
            mixed = false,
        }: {
            ylabel: string | string[]

            abordController?: AbortController
            mixed?: boolean
        } = {
            ylabel: [],
            abordController: undefined,
            mixed: false,
        }
    ): Promise<GraphResponse> {
        return handleServiceCall<GraphResponse>(async () => {
            const { data } = await axios.post(
                `/api/dapi/building/${_id_building}/data/graph`,
                {
                    date_from,
                    date_to,
                    freq,
                    ylabel,
                    datasets,
                    mixed,
                },
                {
                    signal: abordController?.signal,
                }
            )
            return {
                data: data.data,
                sources: data.sources,
            }
        })
    }
    /**
     * Récupère les informations d'une source par son identifiant
     * @param _id_source - Identifiant de la source
     * @param date_from - Date de début au format YYYY-MM-DD
     * @param date_to - Date de fin au format YYYY-MM-DD
     * @param freq - Fréquence (`raw`, `hourly`, `daily`, `weekly`, `monthly`)
     * @param ylabel - Nom de l'axe des ordonnées
     * @param datasets - Les datasets à afficher
     *
     * @returns Les données du graphique Plotly au format JSON string
     */
    async createGraphBySourceId(
        _id_source: string,
        date_from: string,
        date_to: string,
        freq: string,
        datasets: any[],
        {
            ylabel,
            abordController,
            mixed,
        }: {
            ylabel?: string | string[]
            abordController?: AbortController
            mixed?: boolean
        }
    ): Promise<GraphResponse> {
        return handleServiceCall<GraphResponse>(async () => {
            const { data } = await axios.post(
                `/api/dapi/source/${_id_source}/data/graph`,
                {
                    date_from,
                    date_to,
                    freq,
                    ylabel,
                    datasets,
                    mixed,
                },
                {
                    signal: abordController?.signal,
                }
            )
            return data
        })
    }
}

export type { GraphResponse }
